/**
 * useDisclaimer — DISCLAIMER-01
 *
 * The active version plus the caller's acceptance of it (CA-05). Both the
 * locale (CA-04) and the user id are part of the key: a language switch has to
 * refetch the body, and a session change in the same tab must not answer the
 * next user from the previous one's cached acceptance.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useLocale } from '@/i18n';
import { DISCLAIMER_ENDPOINT, fetchDisclaimer } from '../api/legal.api';
import type { Disclaimer } from '../types/legal.types';

export function useDisclaimer() {
  const { user } = useAuth();
  // CA-04: the active language is the one the UI renders in. `users.language`
  // is read nowhere — IntlProvider.tsx pins its user preference to null.
  const { locale } = useLocale();

  return useQuery<Disclaimer>({
    queryKey: [DISCLAIMER_ENDPOINT, user?.id ?? 'anon', locale],
    queryFn: () => fetchDisclaimer(locale),
    // Always enabled: the endpoint enforces auth server-side. The previous
    // `enabled: Boolean(user?.id)` made the query wait for the auth hook
    // to resolve, which on cold CI leaves the page in skeleton for the
    // full Playwright assertion window and the disclaimer body never
    // renders. Keys with `user?.id ?? 'anon'` are still per-user cached.
    enabled: true,
    // The guard fails open on an error, and the app defaults (no retry, no
    // refetch, staleTime Infinity) would then never ask again in this tab. A
    // query with no data is stale whatever its staleTime, so this stops once
    // an answer lands.
    retry: 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}
